import { useState } from "react";
import { toast } from "sonner";
import {
  BillingOperationStatus,
  InvoiceKind,
  InvoiceStatus,
} from "@api/shared-runtime/enums";
import {
  CHARGE_AUTHENTICATION_REQUIRED_MESSAGE,
  CHARGE_IN_FLIGHT_TIMEOUT_MINUTES,
  describePaidWith,
  describeRefund,
} from "@api/shared-runtime/billing-collection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPaymentMethod } from "@/features/organization-billing/payment-method-format";
import { formatUsageBillingCurrency } from "./usage-billing-format";
import { UsageBillingInvoiceDetailSheet } from "./usage-billing-invoice-detail-sheet";
import {
  INVOICE_STATUS_LABELS,
  describeInvoiceCoverage,
  invoiceStatusVariant,
  isDirectCharge,
  type PendingCycle,
  type UnitInvoice,
  type UnitInvoices,
} from "./usage-billing-invoice-presentation";

export type { UnitInvoice, UnitInvoices };
export type UnitPaymentMethod = UnitInvoices["paymentMethod"];

type InvoiceActionKind = "issue" | "charge" | "email" | "resend" | "void";
type ConfirmingAction = { kind: InvoiceActionKind; invoice: UnitInvoice };
type ChargeInvoiceInput = { invoiceId: string; expectedVersion: number };

const BLOCKED_REASON_LABELS = {
  "missing-basis":
    "Blocked: shift events are missing the wage amounts that percentage pricing or provider wage billing requires.",
} as const;

/** Mirrors the server's precondition message so both paths read the same. */
const MISSING_BILLING_ACCOUNT_MESSAGE =
  "This business unit has not saved a billing contact yet. A unit admin must add one under Settings → Billing before the invoice can be issued.";
const MISSING_BILLING_ADDRESS_MESSAGE =
  "This business unit has not saved a billing address yet. A unit admin must add one under Settings → Billing before the invoice can be issued.";
const BLOCKED_ISSUE_TITLE = "Can't issue this invoice yet";
const SALES_TAX_SUFFIX = " plus applicable sales tax";
const AWAITING_PAYMENT_NOTE = "Charge submitted, awaiting payment.";
const CHARGE_IN_PROGRESS_NOTE = "Charge in progress.";
const MINUTE_MS = 60_000;

function isMissingBillingAddress(unit: UnitInvoices): boolean {
  return unit.billingAddressRequired && !unit.hasBillingAddress;
}

function describeCoverage(invoice: UnitInvoice): string {
  return describeInvoiceCoverage(invoice).join(" and ");
}

function describePendingCycle(cycle: PendingCycle): string {
  const coverage = describeInvoiceCoverage(cycle).join(" + ");
  return cycle.kind === InvoiceKind.Closing ? `Closing: ${coverage}` : coverage;
}

function isEmailedInvoice(invoice: UnitInvoice): boolean {
  return invoice.paymentServiceInvoiceId !== null;
}

/** A submitted charge the processor is still settling; nothing else may touch the invoice. */
function isAwaitingPayment(invoice: UnitInvoice): boolean {
  return (
    invoice.chargeOperation?.status === BillingOperationStatus.Succeeded &&
    invoice.lastPaymentError === null
  );
}

/** Mirrors the server rule: an older pending row is a crashed attempt. */
function isChargeInFlight(invoice: UnitInvoice): boolean {
  const operation = invoice.chargeOperation;
  if (operation?.status !== BillingOperationStatus.Pending) {
    return false;
  }
  const startedAt = new Date(operation.updatedAt).getTime();
  return Date.now() - startedAt < CHARGE_IN_FLIGHT_TIMEOUT_MINUTES * MINUTE_MS;
}

/**
 * Read from the invoice, not the operation row: the webhook overwrites the
 * invoice error when the customer's own attempt fails, which is what
 * re-enables a staff retry.
 */
function isAuthenticationRequired(invoice: UnitInvoice): boolean {
  return invoice.lastPaymentError === CHARGE_AUTHENTICATION_REQUIRED_MESSAGE;
}

/**
 * A pending attempt of any age may still have moved money and a settling one
 * already did, so neither can be charged again, emailed, or voided. Mirrors
 * the server's guard.
 */
function isCollectable(invoice: UnitInvoice): boolean {
  return (
    invoice.status === InvoiceStatus.Open &&
    invoice.chargeOperation?.status !== BillingOperationStatus.Pending &&
    !(isDirectCharge(invoice) && isAwaitingPayment(invoice))
  );
}

function isDiscardable(invoice: UnitInvoice): boolean {
  if (invoice.status !== InvoiceStatus.Draft) {
    return false;
  }
  if (invoice.exportOperation?.status === BillingOperationStatus.Pending) {
    return false;
  }
  if (invoice.paymentServiceInvoiceId === null) {
    return true;
  }
  return invoice.exportOperation?.status === BillingOperationStatus.Failed;
}

/**
 * An export that failed after Stripe returned the draft leaves the row on the
 * processor path; issuing it would strand it, so emailing again is the only
 * way forward (the server resumes that draft).
 */
function isEmailRetryable(invoice: UnitInvoice): boolean {
  return (
    invoice.status === InvoiceStatus.Draft &&
    invoice.paymentServiceInvoiceId !== null &&
    invoice.exportOperation?.status === BillingOperationStatus.Failed
  );
}

function isIssuable(invoice: UnitInvoice): boolean {
  return (
    invoice.status === InvoiceStatus.Draft &&
    invoice.paymentServiceInvoiceId === null &&
    invoice.exportOperation?.status !== BillingOperationStatus.Pending
  );
}

function isVoidable(invoice: UnitInvoice): boolean {
  return (
    isCollectable(invoice) &&
    invoice.exportOperation?.status !== BillingOperationStatus.Pending
  );
}

function chargeBlockedReason({
  invoice,
  paymentMethod,
}: {
  invoice: UnitInvoice;
  paymentMethod: UnitPaymentMethod;
}): string | null {
  if (!paymentMethod) {
    return "This business unit has no saved payment method. A unit admin can add one under Settings → Billing, or email the invoice instead.";
  }
  if (isChargeInFlight(invoice)) {
    return "A charge for this invoice is already in progress. Wait for it to settle before retrying.";
  }
  if (isAuthenticationRequired(invoice)) {
    return invoice.lastPaymentError;
  }
  return null;
}

function chargeLabel(invoice: UnitInvoice): string {
  const failed =
    invoice.chargeOperation?.status === BillingOperationStatus.Failed ||
    invoice.lastPaymentError !== null;
  return failed ? "Retry charge" : "Charge saved method";
}

const DIALOG_TITLES: Record<InvoiceActionKind, string> = {
  issue: "Issue this invoice?",
  charge: "Charge the saved payment method?",
  email: "Email this invoice?",
  resend: "Email the invoice again?",
  void: "Void this invoice?",
};

const DIALOG_ACTION_LABELS: Record<InvoiceActionKind, string> = {
  issue: "Issue invoice",
  charge: "Charge now",
  email: "Email invoice",
  resend: "Resend invoice",
  void: "Void invoice",
};

function ConfirmationBody({
  action,
  unitName,
  paymentMethod,
  taxApplies,
}: {
  action: ConfirmingAction;
  unitName: string;
  paymentMethod: UnitPaymentMethod;
  taxApplies: boolean;
}) {
  const { kind, invoice } = action;
  const baseAmount = formatUsageBillingCurrency(
    invoice.amountDue ?? invoice.subtotalAmount,
  );
  const isUntaxedDraft = taxApplies && invoice.status === InvoiceStatus.Draft;
  const amount = isUntaxedDraft
    ? `${baseAmount}${SALES_TAX_SUFFIX}`
    : baseAmount;
  if (kind === "issue") {
    return (
      <>
        {unitName} gets a {amount} invoice covering {describeCoverage(invoice)},
        due in 30 days. Nothing is charged yet: charge the saved method, or the
        unit pays from its billing page.
      </>
    );
  }
  if (kind === "charge") {
    return (
      <>
        {amount} for {describeCoverage(invoice)} will be charged to {unitName}
        {"'"}s saved method
        {paymentMethod ? ` (${formatPaymentMethod(paymentMethod)})` : ""}.
      </>
    );
  }
  if (kind === "email") {
    return (
      <>
        {unitName}
        {"'"}s billing contact will be emailed a {amount} invoice covering{" "}
        {describeCoverage(invoice)}, payable online and due in 30 days. This
        path carries an invoicing fee.
      </>
    );
  }
  if (kind === "void") {
    return (
      <>
        The {amount} invoice covering {describeCoverage(invoice)} will be voided
        and its usage released, so the cycle can be drafted again.
        {isEmailedInvoice(invoice)
          ? " The emailed invoice is voided as well."
          : " Any unfinished payment attempt is canceled."}
      </>
    );
  }
  return (
    <>
      Invoice {invoice.paymentServiceInvoiceNumber ?? invoice.id} covering{" "}
      {describeCoverage(invoice)} will be emailed again to {unitName}
      {"'"}s billing contact.
    </>
  );
}

export function UsageBillingInvoices({
  data,
  unitName,
  canCharge,
  canManageInvoices,
  isGenerating,
  busyInvoiceId,
  onGenerate,
  loadFreshData,
  onIssue,
  onDiscard,
  onCharge,
  onChargeDirect,
  onEmail,
  onResend,
  onVoid,
}: {
  data: UnitInvoices;
  unitName: string;
  canCharge: boolean;
  canManageInvoices: boolean;
  isGenerating: boolean;
  busyInvoiceId: string | null;
  onGenerate: () => void;
  /** Re-reads the unit before blocking, so a change made moments ago counts. */
  loadFreshData: () => Promise<UnitInvoices>;
  onIssue: (invoiceId: string) => void;
  onDiscard: (invoiceId: string) => void;
  /** Pays an emailed invoice with the saved method. */
  onCharge: (input: ChargeInvoiceInput) => void;
  /** Charges or retries an invoice Saile collects itself. */
  onChargeDirect: (input: ChargeInvoiceInput) => void;
  onEmail: (invoiceId: string) => void;
  onResend: (invoiceId: string) => void;
  onVoid: (invoiceId: string) => void;
}) {
  const [confirmingAction, setConfirmingAction] =
    useState<ConfirmingAction | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState<UnitInvoice | null>(
    null,
  );

  // Told at click time rather than with a disabled button, so the admin
  // learns what to do instead of guessing why the action is unavailable.
  const findBlockedReason = async (
    kind: InvoiceActionKind,
    invoice: UnitInvoice,
  ): Promise<{ title: string; reason: string } | null> => {
    if (kind === "issue" || kind === "email") {
      const fresh =
        data.hasBillingAccount && data.hasBillingAddress
          ? data
          : await loadFreshData();
      if (!fresh.hasBillingAccount) {
        return {
          title: BLOCKED_ISSUE_TITLE,
          reason: MISSING_BILLING_ACCOUNT_MESSAGE,
        };
      }
      if (isMissingBillingAddress(fresh)) {
        return {
          title: BLOCKED_ISSUE_TITLE,
          reason: MISSING_BILLING_ADDRESS_MESSAGE,
        };
      }
      return null;
    }
    if (kind === "charge") {
      const fresh = await loadFreshData();
      const current =
        fresh.invoices.find((row) => row.id === invoice.id) ?? invoice;
      if (
        current.status === InvoiceStatus.Draft &&
        isMissingBillingAddress(fresh)
      ) {
        return {
          title: BLOCKED_ISSUE_TITLE,
          reason: MISSING_BILLING_ADDRESS_MESSAGE,
        };
      }
      const reason = chargeBlockedReason({
        invoice: current,
        paymentMethod: fresh.paymentMethod,
      });
      return reason ? { title: "Can't charge this invoice yet", reason } : null;
    }
    return null;
  };

  const confirmAction = async (
    kind: InvoiceActionKind,
    invoice: UnitInvoice,
  ) => {
    const blocked = await findBlockedReason(kind, invoice);
    if (blocked) {
      toast.error(blocked.title, { description: blocked.reason });
      return;
    }
    setConfirmingAction({ kind, invoice });
  };

  const runAction = ({ kind, invoice }: ConfirmingAction) => {
    if (kind === "charge") {
      if (data.billingAccountVersion === null) {
        return;
      }
      const input = {
        invoiceId: invoice.id,
        expectedVersion: data.billingAccountVersion,
      };
      if (isEmailedInvoice(invoice)) {
        onCharge(input);
      } else {
        onChargeDirect(input);
      }
      return;
    }
    const handlers: Record<
      Exclude<InvoiceActionKind, "charge">,
      (invoiceId: string) => void
    > = { issue: onIssue, email: onEmail, resend: onResend, void: onVoid };
    handlers[kind](invoice.id);
  };

  return (
    <section aria-label="Invoices" className="space-y-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Invoices</h3>
          <p className="text-sm text-muted-foreground">
            Plans billed at the start of the cycle get a draft on its first day;
            plans billed after get it the day after the cycle ends. Issuing
            opens the invoice for the unit: it is then charged to the saved
            method, paid by the unit from its billing page, or emailed when
            nothing is saved.
          </p>
        </div>
      </div>

      {data.pendingCycles.length > 0 && (
        <div
          aria-label="Cycles awaiting a draft"
          className="rounded-md border border-dashed p-3 text-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium">
              {data.pendingCycles.length} cycle
              {data.pendingCycles.length === 1 ? "" : "s"} awaiting a draft
            </p>
            {canManageInvoices && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isGenerating}
                onClick={onGenerate}
              >
                {isGenerating ? "Generating..." : "Generate drafts"}
              </Button>
            )}
          </div>
          <ul className="mt-2 space-y-1">
            {data.pendingCycles.map((cycle) => (
              <li key={`${cycle.cycleStart}:${cycle.kind}`}>
                {describePendingCycle(cycle)}
                {cycle.blockedReason && (
                  <span className="ml-2 text-amber-700">
                    {BLOCKED_REASON_LABELS[cycle.blockedReason]}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground">No invoices yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Covers</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.invoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  canCharge={canCharge}
                  canManageInvoices={canManageInvoices}
                  isBusy={busyInvoiceId === invoice.id}
                  hasPaymentMethod={data.paymentMethod !== null}
                  onView={() => setViewingInvoice(invoice)}
                  onConfirm={(kind) => confirmAction(kind, invoice)}
                  onDiscard={() => onDiscard(invoice.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <UsageBillingInvoiceDetailSheet
        invoice={viewingInvoice}
        onOpenChange={(open) => !open && setViewingInvoice(null)}
      />

      <AlertDialog
        open={confirmingAction !== null}
        onOpenChange={(open) => !open && setConfirmingAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmingAction ? DIALOG_TITLES[confirmingAction.kind] : ""}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmingAction && (
                <ConfirmationBody
                  action={confirmingAction}
                  unitName={unitName}
                  paymentMethod={data.paymentMethod}
                  taxApplies={data.billingAddressRequired}
                />
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (!confirmingAction) {
                  return;
                }
                runAction(confirmingAction);
                setConfirmingAction(null);
              }}
            >
              {confirmingAction
                ? DIALOG_ACTION_LABELS[confirmingAction.kind]
                : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/**
 * What collects an open invoice. Direct charge leads whenever a method is
 * saved; the emailed invoice is the only path when nothing is saved, or when
 * the emailed invoice's card insists on customer authentication, which an
 * off-session charge cannot complete.
 */
function primaryCollection({
  invoice,
  hasPaymentMethod,
}: {
  invoice: UnitInvoice;
  hasPaymentMethod: boolean;
}): Exclude<InvoiceActionKind, "issue" | "void"> {
  if (isEmailedInvoice(invoice)) {
    return hasPaymentMethod && !isAuthenticationRequired(invoice)
      ? "charge"
      : "resend";
  }
  if (!hasPaymentMethod || isAuthenticationRequired(invoice)) {
    return "email";
  }
  return "charge";
}

function rowPrimaryAction({
  invoice,
  collectable,
  hasPaymentMethod,
}: {
  invoice: UnitInvoice;
  collectable: boolean;
  hasPaymentMethod: boolean;
}): Exclude<InvoiceActionKind, "issue" | "void"> | null {
  if (collectable) {
    return primaryCollection({ invoice, hasPaymentMethod });
  }
  if (isEmailRetryable(invoice)) {
    return "email";
  }
  return null;
}

function InvoiceRow({
  invoice,
  canCharge,
  canManageInvoices,
  isBusy,
  hasPaymentMethod,
  onView,
  onConfirm,
  onDiscard,
}: {
  invoice: UnitInvoice;
  canCharge: boolean;
  canManageInvoices: boolean;
  isBusy: boolean;
  hasPaymentMethod: boolean;
  onView: () => void;
  onConfirm: (kind: InvoiceActionKind) => void;
  onDiscard: () => void;
}) {
  const exportFailed =
    invoice.exportOperation?.status === BillingOperationStatus.Failed;
  const chargeFailed =
    invoice.chargeOperation?.status === BillingOperationStatus.Failed;
  const collectable = isCollectable(invoice);
  const primary = rowPrimaryAction({ invoice, collectable, hasPaymentMethod });
  const showResendSecondary =
    collectable && isEmailedInvoice(invoice) && primary === "charge";
  const chargeNote =
    invoice.status === InvoiceStatus.Open && !collectable
      ? invoice.chargeOperation?.status === BillingOperationStatus.Pending
        ? CHARGE_IN_PROGRESS_NOTE
        : AWAITING_PAYMENT_NOTE
      : null;
  const paidWith = describePaidWith(invoice);
  const refund = describeRefund(invoice);
  return (
    <TableRow>
      <TableCell>
        <div className="min-w-44 space-y-0.5">
          {describeInvoiceCoverage(invoice).map((line) => (
            <p key={line}>{line}</p>
          ))}
          {invoice.kind === InvoiceKind.Closing && (
            <Badge variant="outline" size="small">
              Closing
            </Badge>
          )}
        </div>
        <button
          type="button"
          onClick={onView}
          className="mt-1 text-xs text-primary underline"
        >
          View details
        </button>
        {exportFailed && invoice.exportOperation?.error && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            Could not email: {invoice.exportOperation.error}
          </p>
        )}
        {chargeFailed && invoice.chargeOperation?.error && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            Charge failed: {invoice.chargeOperation.error}
          </p>
        )}
        {invoice.lastPaymentError && (
          <p className="mt-1 text-xs text-destructive">
            Last payment: {invoice.lastPaymentError}
          </p>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-start gap-1">
          <Badge variant={invoiceStatusVariant(invoice.status)}>
            {INVOICE_STATUS_LABELS[invoice.status]}
          </Badge>
          {refund && <Badge variant="secondary">{refund}</Badge>}
          <span className="text-muted-foreground">
            {paidWith
              ? `Paid with ${paidWith}`
              : isEmailedInvoice(invoice)
                ? (invoice.paymentServiceInvoiceNumber ?? "Emailed invoice")
                : isDirectCharge(invoice)
                  ? "Direct charge"
                  : "Not issued"}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatUsageBillingCurrency(invoice.subtotalAmount)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {invoice.totalAmount
          ? formatUsageBillingCurrency(invoice.totalAmount)
          : "—"}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-col items-end gap-2">
          {canManageInvoices && isIssuable(invoice) && (
            <Button
              type="button"
              size="sm"
              disabled={isBusy}
              onClick={() => onConfirm("issue")}
            >
              {isBusy ? "Issuing..." : "Issue"}
            </Button>
          )}
          {canCharge && primary && (
            <Button
              type="button"
              size="sm"
              disabled={isBusy}
              onClick={() => onConfirm(primary)}
            >
              {isBusy
                ? "Working..."
                : primary === "charge"
                  ? chargeLabel(invoice)
                  : DIALOG_ACTION_LABELS[primary]}
            </Button>
          )}
          {canCharge && showResendSecondary && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isBusy}
              onClick={() => onConfirm("resend")}
            >
              Resend invoice
            </Button>
          )}
          {canManageInvoices && isDiscardable(invoice) && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isBusy}
              onClick={onDiscard}
            >
              Discard draft
            </Button>
          )}
          {canManageInvoices && isVoidable(invoice) && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isBusy}
              onClick={() => onConfirm("void")}
            >
              Void invoice
            </Button>
          )}
        </div>
        {chargeNote && (
          <p className="mt-1 text-xs text-muted-foreground" role="note">
            {chargeNote}
          </p>
        )}
      </TableCell>
    </TableRow>
  );
}
